import { Check, Pencil } from 'lucide-react';
import { useId, useState } from 'react';
import { toAppError } from '../../api/errors';
import { useSaveProfile } from '../../data/queries';
import type { Employee, EmployeeModule, ModuleConnection, ModuleQuestion } from '../../domain/models';
import { isProfileEmpty } from '../../domain/models';
import { useT } from '../../l10n/i18n';
import { errorMessage } from '../core/presentation';
import { Sheet } from '../core/widgets/Sheet';
import { useToast } from '../core/widgets/toasts';
import { LinkedText } from '../item/links';
import { CheckChip, QuestionField, TextField } from './fields';
import { cleanAnswers, fieldId, missingAnswers, serverFieldErrors, without } from './hire';

// What an employee does and what it knows about the business (ADR 0010), on its page. Questions and
// connections are named by the employee's module; answers to questions the module no longer asks keep
// their key as the label.

/** What the employee does, from its module. */
export function SkillsCard({ module }: { module: EmployeeModule }) {
  const t = useT();
  const id = useId();
  return (
    <section className="card emp-card" aria-labelledby={`${id}-title`}>
      <h2 id={`${id}-title`} className="title-medium">
        {t('hireSkills')}
      </h2>
      <ul className="hire-skills">
        {module.skills.map((s) => (
          <li key={s} className="body-medium">
            <Check size={16} strokeWidth={2.6} aria-hidden />
            <span>{s}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** The module's questions, then answers to any it does not ask (labelled by their key). */
function profileQuestions(module: EmployeeModule | undefined, answers: Record<string, string>): ModuleQuestion[] {
  const asked = module?.questions ?? [];
  const extra = Object.keys(answers)
    .filter((k) => !asked.some((q) => q.key === k))
    .map((key): ModuleQuestion => ({ key, label: key, kind: 'long', options: [], required: false }));
  return [...asked, ...extra];
}

/** The module's connections, then any others the profile names (labelled by their key). */
function profileConnections(module: EmployeeModule | undefined, keys: string[]): ModuleConnection[] {
  const known = module?.connections ?? [];
  return [...known, ...keys.filter((k) => !known.some((c) => c.key === k)).map((key) => ({ key, label: key }))];
}

export function ProfileCard({ employee: e, module }: { employee: Employee; module?: EmployeeModule }) {
  const t = useT();
  const id = useId();
  const [editing, setEditing] = useState(false);
  const p = e.profile;
  const answered = profileQuestions(module, p.answers).filter((q) => (p.answers[q.key] ?? '').trim() !== '');
  const connections = profileConnections(module, p.connections).filter((c) => p.connections.includes(c.key));

  return (
    <section className="card emp-card" aria-labelledby={`${id}-title`}>
      <div className="emp-card-head">
        <h2 id={`${id}-title`} className="title-medium">
          {t('profileTitle')}
        </h2>
        {e.canEditProfile && (
          <button type="button" className="btn btn-outlined btn-small" onClick={() => setEditing(true)}>
            <Pencil size={16} aria-hidden />
            {t('actionEdit')}
          </button>
        )}
      </div>
      {isProfileEmpty(p) ? (
        <p className="body-medium muted">{t('profileEmpty')}</p>
      ) : (
        <>
          {answered.length > 0 && (
            <dl className="emp-profile">
              {answered.map((q) => (
                <div key={q.key}>
                  <dt className="body-small">{q.label}</dt>
                  <dd className="body-medium pre-wrap">
                    <LinkedText text={p.answers[q.key] ?? ''} />
                  </dd>
                </div>
              ))}
            </dl>
          )}
          {connections.length > 0 && (
            <div className="emp-profile-block">
              <h3 className="overline">{t('profileConnections')}</h3>
              <ul className="hire-tags">
                {connections.map((c) => (
                  <li key={c.key} className="label-medium">
                    {c.label}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {p.note && (
            <div className="emp-profile-block">
              <h3 className="overline">{t('profileNote')}</h3>
              <p className="body-medium pre-wrap">
                <LinkedText text={p.note} />
              </p>
            </div>
          )}
        </>
      )}
      {editing && <ProfileEditor employee={e} module={module} onClose={() => setEditing(false)} />}
    </section>
  );
}

/** Changes the profile in a sheet, starting from what it says now. Required answers are checked first. */
function ProfileEditor({ employee: e, module, onClose }: { employee: Employee; module?: EmployeeModule; onClose: () => void }) {
  const t = useT();
  const toast = useToast();
  const id = useId();
  const save = useSaveProfile();
  const [answers, setAnswers] = useState<Record<string, string>>(e.profile.answers);
  const [connections, setConnections] = useState<string[]>(e.profile.connections);
  const [note, setNote] = useState(e.profile.note ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const questions = profileQuestions(module, e.profile.answers);
  const options = profileConnections(module, e.profile.connections);
  const busy = save.isPending;
  const domId = (field: string) => `${id}-${field}`;

  const showErrors = (found: Record<string, string>) => {
    setErrors(found);
    const first = questions.map((q) => fieldId.answer(q.key)).find((f) => found[f]);
    if (first) document.getElementById(domId(first))?.focus();
  };

  const submit = async () => {
    if (busy) return;
    const found = missingAnswers(t, questions, answers);
    if (Object.keys(found).length > 0) return showErrors(found);
    try {
      await save.mutateAsync({
        employeeId: e.id,
        profile: { answers: cleanAnswers(answers), connections, note: note.trim() || undefined },
      });
      toast(t('profileSaved'));
      onClose();
    } catch (err) {
      const appError = toAppError(err);
      const { fields, placed } = serverFieldErrors(t, appError, (f) => questions.some((q) => fieldId.answer(q.key) === f));
      if (placed) showErrors(fields);
      else toast(errorMessage(t, appError));
    }
  };

  return (
    <Sheet
      open
      onClose={() => !busy && onClose()}
      title={t('profileTitle')}
      labelledBy={`${id}-title`}
      onSubmit={() => void submit()}
      actions={
        <>
          <button type="button" className="btn btn-text" onClick={onClose} disabled={busy}>
            {t('actionCancel')}
          </button>
          <button type="submit" className="btn btn-filled" disabled={busy}>
            {busy && <span className="iv-spinner" aria-hidden />}
            {t('profileSave')}
          </button>
        </>
      }
    >
      <div className="hire-fields">
        {questions.map((q) => {
          const f = fieldId.answer(q.key);
          return (
            <QuestionField
              key={q.key}
              id={domId(f)}
              question={q}
              value={answers[q.key] ?? ''}
              error={errors[f]}
              disabled={busy}
              onChange={(v) => {
                setAnswers((a) => ({ ...a, [q.key]: v }));
                setErrors((all) => without(all, f));
              }}
            />
          );
        })}
        {options.length > 0 && (
          <fieldset className="hire-field" disabled={busy}>
            <legend className="hire-label">{t('profileConnections')}</legend>
            <div className="hire-chips">
              {options.map((c) => (
                <CheckChip
                  key={c.key}
                  label={c.label}
                  checked={connections.includes(c.key)}
                  onChange={(on) => setConnections((all) => (on ? [...all, c.key] : all.filter((k) => k !== c.key)))}
                />
              ))}
            </div>
          </fieldset>
        )}
        <TextField id={`${id}-note`} label={t('profileNote')} value={note} onChange={setNote} long disabled={busy} />
      </div>
    </Sheet>
  );
}
